import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateGuestDemoMutation } from "@store/api/publicAgenda";
import { trackDemoEvent } from "@utils/demoAnalytics";
import { setDemoGuestToken } from "@utils/guestDemo";
import useToastNotification from "./useToastNotification";

/**
 * Entry point for launching the real backend demo as an anonymous guest
 * (Phase 2). Creates a guest demo agenda, stores its token, and navigates to
 * the public demo route. Returns { startGuestDemo, isGuestDemoLoading }.
 *
 * @param {Object} options
 * @param {string} [options.source] - Analytics tag identifying which trigger fired.
 */
const useStartGuestDemo = ({ source } = {}) => {
  const navigate = useNavigate();
  const { showErrorToast } = useToastNotification();
  const [createGuestDemo, { isLoading: isGuestDemoLoading }] =
    useCreateGuestDemoMutation();

  const startGuestDemo = useCallback(
    async ({ title } = {}) => {
      trackDemoEvent("demo_started", source ? { source } : undefined);
      try {
        const { guestToken, agendaId } = await createGuestDemo(
          title ? { title } : undefined,
        ).unwrap();
        if (guestToken && agendaId) {
          setDemoGuestToken(guestToken);
          navigate(`/demo/${agendaId}?demo=true`);
          return agendaId;
        }
        return null;
      } catch (error) {
        console.error("Failed to start guest demo:", error);
        showErrorToast("Failed to start demo. Please try again.");
        return null;
      }
    },
    [createGuestDemo, navigate, showErrorToast, source],
  );

  return { startGuestDemo, isGuestDemoLoading };
};

export default useStartGuestDemo;
